<template>
  <div
    v-show="!isAppInitialized || !layoutStore.hasChartImageLoadAttemptCompleted"
    class="loading-spinner"
  ><i class="fad fa-fw fa-spinner-third fa-spin"></i></div>
  <div
    v-if="isAppInitialized"
    class="app-content"
    :class="[
      screenTypeClass,
      {
        'drag-n-drop-occurring': layoutStore.isDragNDropping,
        'app-loading': !isAppInitialized
      }
    ]"
  >
    <Notifications />
    <nav class="navbar-container">
      <Navbar />
    </nav>
    <div
      v-show="shouldShowInteractiveTour"
      class="interactive-tour-window-background"
      :class="{
        'is-visible': isTourActive,
      }"
    ></div>
    <div :id="CONTROL_BAR_UPPER_CONTAINER_ID">
      <ControlBarUpper />
    </div>
    <Chart />
    <div class="control-bar-lower-container">
      <ControlBarLower />
    </div>
    <div class="below-chart-content">
      <div class="mode-buttons-container interactive-tour-container">
        <ModeButtons />
        <InteractiveTourWindow
          :page-number="9"
          :arrow-position="InteractiveTourTrianglePosition.LeftCenter"
          :position="{
            left: (dimensions) => `calc(100% + ${dimensions.altitude}px)`,
            bottom: '50%',
            transform: 'translateY(50%)'
          }"
        >
          <template v-slot:main-header>
            <span>Go beyond the charts right from the workbench with other unique panels</span>
          </template>
          <template v-slot:explanation>
            <div>
              <p>
                The new workbench is about much more than just the charts.
                The various panel features at the bottom of the page let you review your ChartLists, add comments for any saved chart, review your technical alerts, and even explore options data for any symbol we cover.
              </p>
              <p>We recommend exploring some of these next-level features, and keep an eye out for more in the months to come as we continue to expand the capabilities of our new SharpCharts Workbench.</p>
            </div>
          </template>
        </InteractiveTourWindow>
      </div>
      <div class="mode-specific-ui-container interactive-tour-container">
        <ModeSpecificUI />
        <InteractiveTourWindow
          :page-number="7"
          :is-wide="true"
          :arrow-position="InteractiveTourTrianglePosition.BottomCenter"
          :position="{
            left: '50%',
            bottom: (dimensions) => `calc(100% + ${dimensions.altitude}px)`,
            transform: 'translateX(-50%)'
          }"
          @visible="onInteractiveTourChartSettingsVisible()"
        >
          <template v-slot:main-header>
            <span>Add technical indicators and overlays to take your chart customizations to the next level</span>
          </template>
          <template v-slot:explanation>
            <div>
              <p>The Chart Settings panel is where you can add dozens of technical indicators and overlays to your charts and tweak various attributes like y-axis scale, candlestick fills, dividend adjustment, pre- and post-market data, and more.</p>
              <p>And remember, all of these indicators, overlays and other settings can be saved as a chart template for future use. That's what we call a “ChartStyle”.</p>
            </div>
          </template>
        </InteractiveTourWindow>
        <InteractiveTourWindow
          :page-number="8"
          :is-wide="true"
          :arrow-position="InteractiveTourTrianglePosition.BottomCenter"
          :position="{
            left: '50%',
            bottom: (dimensions) => `calc(100% + ${dimensions.altitude}px)`,
            transform: 'translateX(-50%)'
          }"
          @visible="onInteractiveChartStyleLibraryVisible()"
        >
          <template v-slot:main-header>
            <span>Discover new charts that have been professionally designed for you with our “ChartStyle Library”</span>
          </template>
          <template v-slot:explanation>
            <div>
              <p>If you're looking for a great way to get up and running with SharpCharts, the “ChartStyle Library” panel has a ton to offer.</p>
              <p>
                Browse the categories under the “Select Library” menu and you'll quickly find charts that serve every technical approach.
                Whether you're looking for specific strategies like Relative Strength or Momentum, timeframe charts for short, mid and long-term analysis, or specialized charts from our expert contributors, you'll find it here.
              </p>
              <p>You can also review your own saved ChartStyles here as their own library view.</p>
            </div>
          </template>
        </InteractiveTourWindow>
      </div>
    </div>
    <div v-if="areFreestarAdsDisplayed" class="freestar-footer-ad-container">
      <FreestarAd ad-id-key="BTF_LEADERBOARD" />
    </div>
    <footer class="footer-container">
      <Footer />
    </footer>
    <ChartNotes />
    <ActionResultMessages />
    <ScrollToTopButton />
    <ApplyChartStyleToChartsModal />
    <SaveChartModal />
    <SaveChartStyleModal />
    <ChartStyleButtonModal />
    <ComingSoonModal />
    <CreatePriceAlertModal />
    <WorkbenchPreferencesModal />
    <DeleteChartModal />
    <DeleteChartStyleModal />
    <EditChartNameModal />
    <EditChartStyleNameModal />
    <EmailModal />
    <CreateAccountModal />
    <UpgradeToExtraModal />
    <UpgradeToProModal />
    <CreateChartListModal />
    <div v-show="shouldShowInteractiveTour">
      <StartInteractiveTourModal />
      <EndInteractiveTourModal />
    </div>
    <LinkToChartModal />
    <GoogleTagManager v-if="isAppInitialized && layoutStore.hasChartImageLoadAttemptCompleted && !isLoggedIn" />
  </div>
</template>

<script lang="ts" setup>
  import { UnwrapRef } from 'vue';
  import { SubscriptionCallbackMutation } from 'pinia';
  import { useChartStore } from '@/stores/chartStore';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useChartNotesV2 } from '@/composables/useChartNotesV2';
  import { useSymbolInformation } from '@/composables/useSymbolInformation';
  import { useIndicatorsAndOverlays } from '@/composables/useIndicatorsAndOverlays';
  import { useBrowserTabHistory } from '@/composables/useBrowserTabHistory';
  import { useUrlParameters } from '@/composables/useUrlParameters';
  import { useUniqueIds } from '@/composables/useUniqueIds';
  import { getSharpCharts2ChartImageUrlData } from '@/composables/getSharpCharts2ChartImageUrlData';
  import { getSharpCharts2ChartImageUrlParametersObject } from '@/composables/getSharpCharts2ChartImageUrlParametersObject';
  import { fetchSharpCharts2InspectorData } from '@/composables/fetchSharpCharts2InspectorData';
  import { stopDocumentBodyFromScrollingWhenModalIsOpen } from '@/composables/stopDocumentBodyFromScrollingWhenModalIsOpen';
  import { refreshChartStyles } from '@/composables/refreshChartStyles';
  import { refreshChartLists } from '@/composables/refreshChartLists';
  import { useScreenSizing } from '@/composables/useScreenSizing';
  import { useLoadChartsAndChartStyles } from '@/composables/useLoadChartsAndChartStyles';
  import { useInteractiveTour, InteractiveTourTrianglePosition} from '@/composables/useInteractiveTour';
  import { useHasChartInitialized } from '@/composables/useHasChartInitialized';
  import { useFreestarAds } from '@/composables/useFreestarAds';
  import { useAccountLevelsMaximumLimits } from '@/composables/useAccountLevelsMaximumLimits';
  import { initializeSampleChartStyleGroups } from '@/composables/initializeSampleChartStyleGroups';
  import { initializeDefaultChartStyle } from '@/composables/initializeDefaultChartStyle';
  import { hasDefaultChartStyleBeenInitialized } from '@/composables/hasDefaultChartStyleBeenInitialized';
  import { INVALID_CHART_RECID, isValidChartRecid } from '@/composables/isValidChartRecid';
  import { useModes } from '@/composables/useModes';
  import { getChart } from '@/composables/getChart';
  import { getSharpCharts2ChartStyle } from '@/composables/getSharpCharts2ChartStyle';
  import { areChartStyleObjectsEqual } from '@/composables/areChartStyleObjectsEqual';
  import { getDefaultSharpCharts2RangeStartDate } from '@/utils/getDefaultSharpCharts2RangeStartDate';
  import { getFormattedYearToDateStartDate } from '@/utils/getFormattedYearToDateStartDate';
  import { wait } from '@/utils/wait';
  import { PromiseCacheMap } from '@/utils/cache-map';
  import { waitForChartComponentToBeMounted } from '@/utils/waitForChartComponentToBeMounted';
  import ScrollToTopButton from '@/components/buttons/ScrollToTopButton.vue';
  import ApplyChartStyleToChartsModal from '@/components/modals/ApplyChartStyleToChartsModal.vue';
  import ChartStyleButtonModal from '@/components/modals/ChartStyleButtonModal.vue';
  import ComingSoonModal from '@/components/modals/ComingSoonModal.vue';
  import CreateAccountModal from '@/components/modals/CreateAccountModal.vue';
  import CreateChartListModal from '@/components/modals/CreateChartListModal.vue';
  import CreatePriceAlertModal from '@/components/modals/CreatePriceAlertModal.vue';
  import DeleteChartModal from '@/components/modals/DeleteChartModal.vue';
  import DeleteChartStyleModal from '@/components/modals/DeleteChartStyleModal.vue';
  import EditChartNameModal from '@/components/modals/EditChartNameModal.vue';
  import EditChartStyleNameModal from '@/components/modals/EditChartStyleNameModal.vue';
  import EmailModal from '@/components/modals/EmailModal.vue';
  import EndInteractiveTourModal from '@/components/modals/EndInteractiveTourModal.vue';
  import LinkToChartModal from '@/components/modals/LinkToChartModal.vue';
  import SaveChartModal from '@/components/modals/SaveChartModal.vue';
  import SaveChartStyleModal from '@/components/modals/SaveChartStyleModal.vue';
  import StartInteractiveTourModal from '@/components/modals/StartInteractiveTourModal.vue';
  import UpgradeToExtraModal from '@/components/modals/UpgradeToExtraModal.vue';
  import UpgradeToProModal from '@/components/modals/UpgradeToProModal.vue';
  import WorkbenchPreferencesModal from '@/components/modals/WorkbenchPreferencesModal.vue';
  import ActionResultMessages from '@/components/page-skeleton/ActionResultMessages.vue';
  import Chart from '@/components/page-skeleton/Chart.vue';
  import ChartNotes from '@/components/page-skeleton/ChartNotes.vue';
  import ControlBarLower from '@/components/page-skeleton/ControlBarLower.vue';
  import ControlBarUpper from '@/components/page-skeleton/ControlBarUpper.vue';
  import Footer from '@/components/page-skeleton/Footer.vue';
  import FreestarAd from '@/components/page-skeleton/FreestarAd.vue';
  import GoogleTagManager from '@/components/page-skeleton/GoogleTagManager.vue';
  import InteractiveTourWindow from '@/components/page-skeleton/InteractiveTourWindow.vue';
  import ModeButtons from '@/components/page-skeleton/ModeButtons.vue';
  import ModeSpecificUI from '@/components/page-skeleton/ModeSpecificUI.vue';
  import Navbar from '@/components/page-skeleton/Navbar.vue';
  import Notifications from '@/components/page-skeleton/Notifications.vue';
  import { CONTROL_BAR_UPPER_CONTAINER_ID } from '@/enums/html-ids';
  import { UP_DOWN_COLOR_OPTION_AUTO } from '@/enums/up-down-color-options';
  import { RANGE_OPTIONS_ARRAY, RANGE_OPTION_PREDEFINED_RANGE, RANGE_OPTION_YEAR_TO_DATE, RANGE_OPTION_SELECT_START_END, getRangeObjFromRangeTitle } from '@/enums/range-options';
  import { KAGI_REVERSAL_UNITS_ARRAY } from '@/enums/kagi-reversal-unit-options';
  import { RENKO_BOX_SIZE_UNIT_OPTIONS_ARRAY } from '@/enums/renko-box-size-unit-options';
  import { PERIOD_OPTION_HOURLY, PERIOD_OPTIONS_ARRAY } from '@/enums/period-options';
  import { ChartStore } from '@/types/ChartStore';
  import { LayoutStore } from '@/types/LayoutStore';

  const chartStore = useChartStore();
  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { isLoggedIn, addMemberAuthenticationCallback } = useAuthentication();
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();
  const { getSymbolInformation, isCryptoSymbolInformation } = useSymbolInformation();
  const { getIndicatorsCopyWithResetColors, getOverlaysCopyWithResetColors } = useIndicatorsAndOverlays();
  const { addNewEntryToBrowserTabHistory, loadChartFromBrowserTabHistory } = useBrowserTabHistory();
  const { getSymbolUrlParameterValue, getStyleCacheIdUrlParameterValue, getChartRecidUrlParameterValue, getStyleCacheSettingsOverridesUrlParameterValues } = useUrlParameters();
  const { getPositiveUniqueId } = useUniqueIds();
  const { screenTypeClass, shouldShowInteractiveTour } = useScreenSizing();
  const { loadChart, loadChartStyle } = useLoadChartsAndChartStyles();
  const { isTourActive } = useInteractiveTour();
  const { setHasLoadedInitialChartData, setHasChartUrlCreationAttemptCompleted, isAppInitialized } = useHasChartInitialized();
  const { areFreestarAdsDisplayed } = useFreestarAds();
  const { initializeAccountLevelsMaximumLimits } = useAccountLevelsMaximumLimits();
  const { CHART_SETTINGS_MODE, CHART_STYLE_LIBRARY_MODE, initializeUIBelowChartBasedOnUrlParameters, setMode } = useModes();
  const updateChartPromisesCache = new PromiseCacheMap();

  async function loadChartFromDefaultChartStyle(): Promise<void> {
    if (!isLoggedIn.value) {
      throw new Error('Unable to load chart from default ChartStyle - user is not logged-in.');
    }

    window.sc3WorkbenchLogger.info('Attempting to load chart from default ChartStyle...');

    await initializeDefaultChartStyle();

    if (!hasDefaultChartStyleBeenInitialized()) {
      throw new Error('Unable to load chart from default ChartStyle - failed to initialize default ChartStyle.');
    }

    const symbol = getSymbolUrlParameterValue() || chartStore.symbol;

    await loadChartStyle(savedChartsStore.recidOfDefaultChartStyle, symbol, { shouldThrowErrorOnFailure: true });

    const urlQueryString = window.location.search;
    const styleCacheSettingsOverrides = getStyleCacheSettingsOverridesUrlParameterValues(urlQueryString);

    window.sc3WorkbenchLogger.info(`Applying URL parameter styleCacheSettingsOverrides: ${urlQueryString} to chartStore...`);
    chartStore.$patch(styleCacheSettingsOverrides);

    window.sc3WorkbenchLogger.info(`Successfully loaded chart from default ChartStyle along with symbol: ${symbol} and styleCacheSettingsOverrides: ${urlQueryString}.`);
  }

  async function loadChartFromCurrentChartStoreState(): Promise<void> {
    window.sc3WorkbenchLogger.info('Attempting to load chart from current ChartStore state...');

    const symbolUrlParameterValue = getSymbolUrlParameterValue();

    if (symbolUrlParameterValue && symbolUrlParameterValue !== chartStore.symbol) {
      chartStore.$patch({ symbol: symbolUrlParameterValue });
    } else {
      await updateChartImage(false);
    }

    const urlQueryString = window.location.search;
    const styleCacheSettingsOverrides = getStyleCacheSettingsOverridesUrlParameterValues(urlQueryString);

    window.sc3WorkbenchLogger.info(`Applying URL parameter styleCacheSettingsOverrides: ${urlQueryString} to chartStore...`);
    chartStore.$patch(styleCacheSettingsOverrides);

    window.sc3WorkbenchLogger.info(`Successfully loaded chart from current ChartStore state with${symbolUrlParameterValue ? (' URL symbol: ' + symbolUrlParameterValue) : 'out URL symbol (none was found)'} and URL styleCacheSettingsOverrides: ${urlQueryString}.`);
  }

  async function updateChartImage(isBrowserTabHistoryChartBeingLoaded: boolean): Promise<void> {
    const uniqueId = getPositiveUniqueId();
    const updateChartPromise = _updateChartImage(isBrowserTabHistoryChartBeingLoaded);

    updateChartPromisesCache.set(`${uniqueId}`, updateChartPromise);

    return await updateChartPromise;
  }

  async function _updateChartImage(isBrowserTabHistoryChartBeingLoaded: boolean): Promise<void> {
    try {
      const currentChart = chartStore.chart;
      const updateChartTimeStamp = Date.now();
      lastUpdateChartTimeStamp = updateChartTimeStamp;

      const sharpCharts2ChartImageUrlData = await getSharpCharts2ChartImageUrlData(false);

      if (
        lastUpdateChartTimeStamp !== updateChartTimeStamp &&
        !areChartStyleObjectsEqual(currentChart, chartStore.chart)
      ) {
        return;
      }

      layoutStore.$patch({
        ...sharpCharts2ChartImageUrlData,
        chartImageUrlStringRandomNumber: layoutStore.hasChartImageLoadAttemptCompleted ? Date.now() : layoutStore.chartImageUrlStringRandomNumber
      });

      if (layoutStore.isInspecting) {
        await updateSharpCharts2InspectorData(updateChartTimeStamp);
      }

      addNewEntryToBrowserTabHistory(isBrowserTabHistoryChartBeingLoaded);

      document.title = `${chartStore.symbol} | SharpCharts | StockCharts.com`;
    } catch (e) {
      window.sc3WorkbenchLogger.error('Error while loading chart image', e);
    }

    setHasChartUrlCreationAttemptCompleted(true);
  }

  async function updateSharpCharts2InspectorData(updateChartTimeStamp?: number): Promise<void> {
    const sharpCharts2ChartImageUrlDataCopy = layoutStore.sharpCharts2ChartImageUrlData;

    if (!lastUpdateChartTimeStamp) {
      return;
    }

    const sharpCharts2ChartImageUrlParametersObject = getSharpCharts2ChartImageUrlParametersObject(true, sharpCharts2ChartImageUrlDataCopy);
    const inspectorDataString = await fetchSharpCharts2InspectorData(sharpCharts2ChartImageUrlParametersObject);

    if (
      layoutStore.isInspecting &&
      (!updateChartTimeStamp || lastUpdateChartTimeStamp === updateChartTimeStamp)
    ) {
      layoutStore.$patch({ inspectorDataString });
    }
  }

  async function handleChartStoreChanged(mutation: SubscriptionCallbackMutation<ChartStore>, state: UnwrapRef<ChartStore>): Promise<void> {
    latestHandleChartStoreChangedTimeStamp = Date.now();
    const latestHandleChartStoreChangedTimeStampCopy = latestHandleChartStoreChangedTimeStamp;
    const hasLatestLoadChartTimeStampChanged = lastLatestLoadChartTimeStamp !== state.latestLoadChartTimeStamp;
    lastLatestLoadChartTimeStamp = state.latestLoadChartTimeStamp;
    const hasLatestLoadChartStyleTimeStampChanged = lastLatestLoadChartStyleTimeStamp !== state.latestLoadChartStyleTimeStamp;
    lastLatestLoadChartStyleTimeStamp = state.latestLoadChartStyleTimeStamp;
    const hasLatestLoadBrowserTabHistoryChartTimeStampChanged = lastLatestLoadBrowserTabHistoryChartTimeStamp !== state.latestLoadBrowserTabHistoryChartTimeStamp;
    lastLatestLoadBrowserTabHistoryChartTimeStamp = state.latestLoadBrowserTabHistoryChartTimeStamp;
    const hasLatestLoadUrlStyleCacheIdParameterChartTimeStampChanged = lastLatestLoadUrlStyleCacheIdParameterChartTimeStamp !== state.latestLoadUrlStyleCacheIdParameterChartTimeStamp;
    lastLatestLoadUrlStyleCacheIdParameterChartTimeStamp = state.latestLoadUrlStyleCacheIdParameterChartTimeStamp;
    const hasLatestLoadConvertedPerfChartTimeStampChanged = lastLatestLoadConvertedPerfChartTimeStamp !== state.latestLoadConvertedPerfChartTimeStamp;
    lastLatestLoadConvertedPerfChartTimeStamp = state.latestLoadConvertedPerfChartTimeStamp;
    const isChartBeingLoaded = hasLatestLoadChartTimeStampChanged;
    const isChartStyleBeingLoaded = !isChartBeingLoaded && hasLatestLoadChartStyleTimeStampChanged;
    const isSavedChartOrChartStyleBeingLoaded = isChartBeingLoaded || isChartStyleBeingLoaded;
    const isBrowserTabHistoryChartBeingLoaded = !isSavedChartOrChartStyleBeingLoaded && hasLatestLoadBrowserTabHistoryChartTimeStampChanged;
    const isUrlStyleCacheIdParameterChartBeingLoaded = !isSavedChartOrChartStyleBeingLoaded && !hasLatestLoadBrowserTabHistoryChartTimeStampChanged && hasLatestLoadUrlStyleCacheIdParameterChartTimeStampChanged;
    const isConvertedPerfChartBeingLoaded = !isSavedChartOrChartStyleBeingLoaded && !hasLatestLoadBrowserTabHistoryChartTimeStampChanged && !hasLatestLoadUrlStyleCacheIdParameterChartTimeStampChanged && hasLatestLoadConvertedPerfChartTimeStampChanged;
    const isChartOrChartStyleBeingLoaded = isSavedChartOrChartStyleBeingLoaded || isBrowserTabHistoryChartBeingLoaded || isUrlStyleCacheIdParameterChartBeingLoaded || isConvertedPerfChartBeingLoaded;

    const hasColorSchemeChanged = lastColorScheme !== state.colorScheme;
    lastColorScheme = state.colorScheme;

    if (hasColorSchemeChanged && !isChartOrChartStyleBeingLoaded) {
      chartStore.$patch({
        upColor: UP_DOWN_COLOR_OPTION_AUTO,
        downColor: UP_DOWN_COLOR_OPTION_AUTO,
        sortedIndicators: getIndicatorsCopyWithResetColors(chartStore.sortedIndicators),
        sortedOverlays: getOverlaysCopyWithResetColors(chartStore.sortedOverlays),
        latestChangeColorSchemeTimeStamp: Date.now()
      });

      return;
    }

    const hasRangeChanged = lastRange !== state.range;
    lastRange = state.range;
    const hasRangeDaysChanged = lastRangeDays !== state.rangeDays;
    lastRangeDays = state.rangeDays;
    const hasRangeMonthsChanged = lastRangeMonths !== state.rangeMonths;
    lastRangeMonths = state.rangeMonths;
    const hasRangeYearsChanged = lastRangeYears !== state.rangeYears;
    lastRangeYears = state.rangeYears;
    const hasRangeBarChanged = lastRangeBar !== state.rangeBar;
    lastRangeBar = state.rangeBar;
    const hasRangeGapChanged = lastRangeGap !== state.rangeGap;
    lastRangeGap = state.rangeGap;
    const hasRangeStartDateChanged = lastRangeStartDate !== state.rangeStartDate;
    lastRangeStartDate = state.rangeStartDate;
    const hasRangeEndDateChanged = lastRangeEndDate !== state.rangeEndDate;
    lastRangeEndDate = state.rangeEndDate;

    if (
      (hasRangeChanged || hasRangeDaysChanged || hasRangeMonthsChanged || hasRangeYearsChanged || hasRangeBarChanged || hasRangeGapChanged || hasRangeStartDateChanged || hasRangeEndDateChanged) &&
      !isChartOrChartStyleBeingLoaded
    ) {
      let rangeOption = getRangeObjFromRangeTitle(state.range);
      const formattedYearToDateStartDate = getFormattedYearToDateStartDate();
      let rangeDays = state.rangeDays;
      let rangeMonths = state.rangeMonths;
      let rangeYears = state.rangeYears;
      let rangeBar = state.rangeBar;
      let rangeGap = state.rangeGap;
      let rangeStartDate = state.rangeStartDate;
      let rangeEndDate = state.rangeEndDate;

      if (hasRangeChanged) {
        rangeDays = rangeOption.numberOfDays;
        rangeMonths = rangeOption.numberOfMonths;
        rangeYears = rangeOption.numberOfYears;
        rangeBar = rangeOption.barValue;
        rangeGap = rangeOption.gapValue;
        rangeStartDate = rangeOption === RANGE_OPTION_YEAR_TO_DATE ? formattedYearToDateStartDate : getDefaultSharpCharts2RangeStartDate();
        rangeEndDate = '';
      }

      if (hasRangeDaysChanged) {
        rangeDays = state.rangeDays;
      }

      if (hasRangeMonthsChanged) {
        rangeMonths = state.rangeMonths;
      }

      if (hasRangeYearsChanged) {
        rangeYears = state.rangeYears;
      }

      if (hasRangeBarChanged) {
        rangeBar = state.rangeBar;
      }

      if (hasRangeGapChanged) {
        rangeGap = state.rangeGap;
      }

      if (hasRangeStartDateChanged || state.isMakingEntireStartEndDateChange) {
        rangeStartDate = state.rangeStartDate;
      }

      if (hasRangeEndDateChanged || state.isMakingEntireStartEndDateChange) {
        rangeEndDate = state.rangeEndDate;
      }

      if (rangeOption.useNumberOfDays) {
        if (rangeOption !== RANGE_OPTION_PREDEFINED_RANGE) {
          if (
            rangeDays !== rangeOption.numberOfDays ||
            rangeMonths !== rangeOption.numberOfMonths ||
            rangeYears !== rangeOption.numberOfYears
          ) {
            rangeOption = RANGE_OPTION_PREDEFINED_RANGE;
          }
        }

        if (rangeOption === RANGE_OPTION_PREDEFINED_RANGE) {
          const alternateRangeOption = RANGE_OPTIONS_ARRAY.find(r =>
            !!r?.useNumberOfDays &&
            r !== RANGE_OPTION_PREDEFINED_RANGE &&
            r.numberOfDays === rangeDays &&
            r.numberOfMonths === rangeMonths &&
            r.numberOfYears === rangeYears
          );

          if (alternateRangeOption) {
            rangeOption = alternateRangeOption;
          }
        }
      }

      if (rangeOption.useStartDateAndEndDate) {
        rangeOption = rangeStartDate === formattedYearToDateStartDate && rangeEndDate === '' ? RANGE_OPTION_YEAR_TO_DATE : RANGE_OPTION_SELECT_START_END;
      }

      lastRange = rangeOption.title;
      lastRangeDays = rangeDays;
      lastRangeMonths = rangeMonths;
      lastRangeYears = rangeYears;
      lastRangeBar = rangeBar;
      lastRangeGap = rangeGap;
      lastRangeStartDate = rangeStartDate;
      lastRangeEndDate = rangeEndDate;

      chartStore.$patch({
        range: lastRange,
        rangeDays: lastRangeDays,
        rangeMonths: lastRangeMonths,
        rangeYears: lastRangeYears,
        rangeBar: lastRangeBar,
        rangeGap: lastRangeGap,
        rangeStartDate: lastRangeStartDate,
        rangeEndDate: lastRangeEndDate,
        isMakingEntireStartEndDateChange: false
      });

      return;
    }

    if (state.isMakingEntireStartEndDateChange) {
      chartStore.$patch({ isMakingEntireStartEndDateChange: false });
      return;
    }

    const hasKagiReversalUnitChanged = lastKagiReversalUnit !== state.kagiUnit;
    lastKagiReversalUnit = state.kagiUnit;
    const kagiReversalUnitOption = KAGI_REVERSAL_UNITS_ARRAY.find(option => option.sharpCharts2Id === state.kagiUnit);

    if (hasKagiReversalUnitChanged && !isChartOrChartStyleBeingLoaded && kagiReversalUnitOption) {
      chartStore.$patch({ kagiReversal: kagiReversalUnitOption.defaultKagiReversalValue });

      return;
    }

    const hasRenkoBoxSizeUnitChanged = lastRenkoBoxSizeUnit !== state.renkoUnit;
    lastRenkoBoxSizeUnit = state.renkoUnit;
    const renkoBoxSizeUnitOption = RENKO_BOX_SIZE_UNIT_OPTIONS_ARRAY.find(option => option.sharpCharts2Id === state.renkoUnit);

    if (hasRenkoBoxSizeUnitChanged && !isChartOrChartStyleBeingLoaded && renkoBoxSizeUnitOption) {
      chartStore.$patch({ renkoBoxSize: renkoBoxSizeUnitOption.defaultRenkoBoxSize });

      return;
    }

    const hasPeriodChanged = lastPeriod !== state.period;
    lastPeriod = state.period;

    if (hasPeriodChanged && !isChartOrChartStyleBeingLoaded) {
      const periodObject = PERIOD_OPTIONS_ARRAY.find(p => p.value === state.period);

      if (periodObject) {
        const firstEquivalentPeriodObject = PERIOD_OPTIONS_ARRAY.find(p => p.sharpCharts2Id === periodObject.sharpCharts2Id);

        if (firstEquivalentPeriodObject && firstEquivalentPeriodObject.value !== state.period) {
          chartStore.$patch({ period: firstEquivalentPeriodObject.value });

          return;
        }
      }
    }

    const hasSymbolChanged = lastSymbol !== state.symbol;

    if (hasSymbolChanged && !isChartOrChartStyleBeingLoaded) {
      let symbolInformation;

      try {
        symbolInformation = await getSymbolInformation(state.symbol);
      } catch (e) {
        window.sc3WorkbenchLogger.debug(`Error occurred while getting symbol information for symbol: ${state.symbol} to determine if symbol is a crypto symbol in App.vue.`, e);
      }

      if (latestHandleChartStoreChangedTimeStampCopy !== latestHandleChartStoreChangedTimeStamp) {
        return;
      }

      const patchObject: Partial<ChartStore> = { recidOfChartBeingViewed: INVALID_CHART_RECID };

      if (symbolInformation) {
        const isCrypto = isCryptoSymbolInformation(symbolInformation);
        const periodObject = PERIOD_OPTIONS_ARRAY.find(p => p.value === state.period);
        const isPeriodCryptoOnly = periodObject ? periodObject.isCryptoOnly : false;
        const isPeriodNonCryptoOnly = periodObject ? periodObject.isNonCryptoOnly : false;

        if (
          (isCrypto && isPeriodNonCryptoOnly) ||
          (!isCrypto && isPeriodCryptoOnly)
        ) {
          patchObject.period = PERIOD_OPTION_HOURLY.value;
        }
      }

      if (lastRecidOfLoadSymbolOnlyChartBeingViewed === state.recidOfLoadSymbolOnlyChartBeingViewed) {
        patchObject.recidOfLoadSymbolOnlyChartBeingViewed = INVALID_CHART_RECID;
      }

      lastRecidOfLoadSymbolOnlyChartBeingViewed = state.recidOfLoadSymbolOnlyChartBeingViewed;
      lastSymbol = state.symbol;

      chartStore.$patch(patchObject);

      return;
    }

    lastRecidOfLoadSymbolOnlyChartBeingViewed = state.recidOfLoadSymbolOnlyChartBeingViewed;
    lastSymbol = state.symbol;

    await updateChartImage(isBrowserTabHistoryChartBeingLoaded);
  }

  async function handleLayoutStoreChanged(mutation: SubscriptionCallbackMutation<LayoutStore>, state: UnwrapRef<LayoutStore>): Promise<void> {
    stopDocumentBodyFromScrollingWhenModalIsOpen();

    const hasIsInspectingValueChanged = lastIsInspectingValue !== state.isInspecting;
    lastIsInspectingValue = state.isInspecting;
    const hasChartImageUrlStringRandomNumberChanged = lastChartImageUrlStringRandomNumber !== state.chartImageUrlStringRandomNumber;
    lastChartImageUrlStringRandomNumber = state.chartImageUrlStringRandomNumber;

    if (hasIsInspectingValueChanged) {
      if (state.isInspecting) {
        await updateSharpCharts2InspectorData();
      } else {
        const layoutStorePatchObject: Partial<LayoutStore> = { inspectorDataString: '' };

        if (
          !isActivatingAnnotationMode.value &&
          !isAnnotating.value
        ) {
          layoutStorePatchObject.chartImageUrlStringRandomNumber = Date.now();
        }

        layoutStore.$patch(layoutStorePatchObject);
      }
    } else if (hasChartImageUrlStringRandomNumberChanged && state.isInspecting) {
      await updateSharpCharts2InspectorData();
    }
  }

  async function loadChartFromUrlParameters(): Promise<void> {
    const symbolUrlParameterValue = getSymbolUrlParameterValue();
    const styleCacheId = getStyleCacheIdUrlParameterValue();
    const chartRecid = getChartRecidUrlParameterValue();
    const urlQueryString = window.location.search;
    const styleCacheSettingsOverrides = getStyleCacheSettingsOverridesUrlParameterValues(urlQueryString);

    window.sc3WorkbenchLogger.info(`Attempting to load chart from URL parameters with symbol: ${symbolUrlParameterValue} and styleCacheId: ${styleCacheId} and chartRecid: ${chartRecid} and styleCacheSettingsOverrides: ${urlQueryString}...`);

    let symbol = symbolUrlParameterValue;
    let successfullyLoadedChartSettingsFromChartRecid = false;
    let successfullyLoadedChartSettingsFromStyleCacheId = false;

    if (isValidChartRecid(chartRecid)) {
      window.sc3WorkbenchLogger.info(`Attempting to get chart object from URL parameters with recid: ${chartRecid}...`);
      const chart = await getChart(chartRecid);

      if (!chart) {
        window.sc3WorkbenchLogger.error(`Chart object from URL parameters with recid: ${chartRecid} was not found.`);
      } else if (chart.isPointAndFigureChart) {
        window.sc3WorkbenchLogger.error(`Chart object from URL parameters with recid: ${chartRecid} is a point-and-figure chart. Unable to load chart.`);
      } else if (symbol && chart.symbol !== symbol) {
        window.sc3WorkbenchLogger.error(`Chart object from URL parameters with recid: ${chartRecid} was found but chart.symbol: ${chart.symbol} does not match URL parameter symbol: ${symbol}. Unable to load chart.`);
      } else {
        window.sc3WorkbenchLogger.info(`Attempting to load chart from URL parameters with recid: ${chart.recid} and symbol: ${chart.symbol} and styleCacheId: ${chart.styleCacheId}...`);

        try {
          await loadChart(chart.recid, { shouldThrowErrorOnFailure: true });

          symbol = chart.symbol;
          successfullyLoadedChartSettingsFromChartRecid = true;
        } catch (e) {
          window.sc3WorkbenchLogger.error(`Error occurred while loading chart from URL parameters with recid: ${chart.recid} and symbol: ${chart.symbol} and styleCacheId: ${chart.styleCacheId}.`, e);
        }
      }

      if (
        !chart?.isPointAndFigureChart &&
        (!chart || !symbol || chart.symbol === symbol)
      ) {
        chartStore.$patch({
          recidOfChartBeingViewed: chartRecid,
          recidOfLoadSymbolOnlyChartBeingViewed: chartRecid
        });
      }
    } else {
      window.sc3WorkbenchLogger.info('No valid URL parameter chartRecid was found.');
    }

    if (!symbol) {
      symbol = chartStore.symbol;
      window.sc3WorkbenchLogger.info(`No valid URL parameter symbol was found. Proceeding with chartStore.symbol: ${symbol} instead.`);
    }

    if (styleCacheId) {
      window.sc3WorkbenchLogger.info(`Attempting to load chart settings from URL parameters for styleCacheId: ${styleCacheId}...`);

      const [chartStyleJson] = await Promise.all([
        getSharpCharts2ChartStyle(styleCacheId).catch((e) => {
          window.sc3WorkbenchLogger.debug(`Error occurred while getting SharpCharts2 chart or ChartStyle json from URL parameters for styleCacheId: ${styleCacheId} and symbol: ${symbol}.`, e);
        }),
        getSymbolInformation(symbol).catch((e) => {
          window.sc3WorkbenchLogger.debug(`Error occurred while getting symbol information for symbol: ${symbol} while attempting to load chart from URL parameters for styleCacheId: ${styleCacheId}.`, e);
        })
      ]);

      if (!chartStyleJson) {
        window.sc3WorkbenchLogger.error(`No chart settings were found from URL parameter styleCacheId: ${styleCacheId}.`);
      } else {
        window.sc3WorkbenchLogger.info(`Applying chart settings from URL parameters from styleCacheId: ${styleCacheId} to chartStore...`);

        chartStore.$patch({
          ...chartStyleJson,
          symbol,
          latestLoadUrlStyleCacheIdParameterChartTimeStamp: Date.now()
        });

        successfullyLoadedChartSettingsFromStyleCacheId = true;
      }
    } else {
      window.sc3WorkbenchLogger.info('No valid URL parameter styleCacheId was found.');
    }

    window.sc3WorkbenchLogger.info(`Applying URL parameter symbol: ${symbol} and styleCacheSettingsOverrides: ${urlQueryString} to chartStore...`);
    chartStore.$patch({
      ...styleCacheSettingsOverrides,
      symbol
    });

    if (!successfullyLoadedChartSettingsFromChartRecid && !successfullyLoadedChartSettingsFromStyleCacheId) {
      throw new Error(`Unable to load chart from url parameters - attempts to load chart settings from chartRecid and styleCacheId url parameters were unsuccessful.`);
    }

    window.sc3WorkbenchLogger.info(`Successfully loaded chart from URL parameters with symbol: ${symbol} and styleCacheId: ${styleCacheId} and chartRecid: ${chartRecid} and styleCacheSettingsOverrides: ${urlQueryString}.`);
  }

  async function init(): Promise<void> {
    initializeUIBelowChartBasedOnUrlParameters();

    chartStore.$subscribe(handleChartStoreChanged);
    layoutStore.$subscribe(handleLayoutStoreChanged);

    await loadChartFromUrlParameters()
      .catch(loadChartFromBrowserTabHistory)
      .catch(loadChartFromDefaultChartStyle)
      .catch(loadChartFromCurrentChartStoreState);

    await wait(0);

    const currentUpdateChartPromises = Array.from(updateChartPromisesCache.values());

    await Promise.all(currentUpdateChartPromises);

    setHasLoadedInitialChartData(true);
    addNewEntryToBrowserTabHistory(true);

    await waitForChartComponentToBeMounted();

    await Promise.all([
      refreshChartStyles(),
      initializeDefaultChartStyle(),
      refreshChartLists(),
      initializeAccountLevelsMaximumLimits(),
      initializeSampleChartStyleGroups()
    ]);
  }

  function onInteractiveTourChartSettingsVisible() {
    setMode(CHART_SETTINGS_MODE);
  }

  function onInteractiveChartStyleLibraryVisible() {
    setMode(CHART_STYLE_LIBRARY_MODE);
  }

  let lastColorScheme = chartStore.colorScheme;
  let lastRange = chartStore.range;
  let lastRangeDays = chartStore.rangeDays;
  let lastRangeMonths = chartStore.rangeMonths;
  let lastRangeYears = chartStore.rangeYears;
  let lastRangeBar = chartStore.rangeBar;
  let lastRangeGap = chartStore.rangeGap;
  let lastRangeStartDate = chartStore.rangeStartDate;
  let lastRangeEndDate = chartStore.rangeEndDate;
  let lastKagiReversalUnit = chartStore.kagiUnit;
  let lastRenkoBoxSizeUnit = chartStore.renkoUnit;
  let lastSymbol = chartStore.symbol;
  let lastPeriod = chartStore.period;
  let lastIsInspectingValue = layoutStore.isInspecting;
  let lastLatestLoadChartTimeStamp = chartStore.latestLoadChartTimeStamp;
  let lastLatestLoadChartStyleTimeStamp = chartStore.latestLoadChartStyleTimeStamp;
  let lastLatestLoadBrowserTabHistoryChartTimeStamp = chartStore.latestLoadBrowserTabHistoryChartTimeStamp;
  let lastLatestLoadUrlStyleCacheIdParameterChartTimeStamp = chartStore.latestLoadUrlStyleCacheIdParameterChartTimeStamp;
  let lastLatestLoadConvertedPerfChartTimeStamp = chartStore.latestLoadConvertedPerfChartTimeStamp;
  let latestHandleChartStoreChangedTimeStamp = -1;
  let lastChartImageUrlStringRandomNumber = layoutStore.chartImageUrlStringRandomNumber;
  let lastRecidOfLoadSymbolOnlyChartBeingViewed = chartStore.recidOfLoadSymbolOnlyChartBeingViewed;
  let lastUpdateChartTimeStamp = 0;

  addMemberAuthenticationCallback(init);
</script>

<style>
  :root {
    --mode-buttons-container-width: 180px;
    --sc3-fixed-height: 24px;
    --chart-style-button-tooltip-height: 22px;
    --sc3-compact-height: 24px;
  }

  * {
    box-sizing: border-box;
  }

  b {
    font-weight: bolder;
  }

  .loading-spinner {
    font-size: 20px;
    position: fixed;
    z-index: 3000;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    background-color: #f2f5f7;
  }

  .loading-spinner i {
    position: absolute;
    font-size: 5em;
    top: calc(50% - 50px); /* 50px is 18px (the font-size of the container) multiplied by 5 (the font-size of the icon) all divided by 2 (because we want the icon centered vertically) */
    left: calc(50% - 50px); /* 50px is 18px (the font-size of the container) multiplied by 5 (the font-size of the icon) all divided by 2 (because we want the icon centered horizontally too) */
  }

  .fa-spin {
    -webkit-animation: fa-spin 1s infinite linear;
    animation: fa-spin 1s infinite linear;
  }

  .app-content.app-loading {
    height: 100vh;
    overflow: hidden;
  }

  .drag-n-drop-occurring * {
    user-select: none !important;
  }

  body {
    margin: 0;
    font-size: 0px;
    text-align: left;
    background-color: #0e263e;
  }

  body.modal-open {
    overflow: hidden;
  }

  body, input, select, button, a, textarea {
    font-family: 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }

  input, select, button, a, textarea {
    outline: none;
    box-shadow: none;
    color: #707f8f;
  }

  button, a {
    border-radius: 0px;
    text-align: center;
  }

  button, a, select {
    cursor: pointer;
  }

  input[type=text],
  input[type=email],
  input[type=password],
  input[type=date],
  select,
  textarea,
  div.sc3-text-input {
    border: 2px solid #c0ced8;
    border-radius: 4px;
    padding: 0;
    margin: 0 4px 0 0;
    font-weight: bold;
  }

  input {
    height: 30px;
    padding-left: 6px;
  }

  input[type=range] {
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: transparent;
    cursor: pointer;
    padding: 0px;
  }

  input[type=range]::-webkit-slider-thumb {
    -webkit-appearance: none;
    -moz-appearance: none;
    position: relative;
    top: -4px;
    z-index: 700;
    background-color: #1282f3;
    border-radius: 50%;
    height: 16px;
    width: 16px;
    cursor: pointer;
  }

  input[type=range]::-moz-range-thumb {
    -webkit-appearance: none;
    -moz-appearance: none;
    position: relative;
    top: -4px;
    z-index: 700;
    background-color: #1282f3;
    border-radius: 50%;
    height: 16px;
    width: 16px;
    cursor: pointer;
  }

  input[type=range]::-ms-thumb {
    -webkit-appearance: none;
    -moz-appearance: none;
    position: relative;
    top: -4px;
    z-index: 700;
    background-color: #1282f3;
    border-radius: 50%;
    height: 16px;
    width: 16px;
    cursor: pointer;
  }

  input[type=range]::-webkit-slider-runnable-track {
    -webkit-appearance: none;
    -moz-appearance: none;
    height: 8px;
    border-radius: 4px;
    background-color: #c6ccd2;
    cursor: pointer;
  }

  input[type=range]::-moz-range-track {
    -webkit-appearance: none;
    -moz-appearance: none;
    height: 8px;
    border-radius: 4px;
    background-color: #c6ccd2;
    cursor: pointer;
  }

  input[type=range]::-ms-track {
    -webkit-appearance: none;
    -moz-appearance: none;
    height: 8px;
    border-radius: 4px;
    background-color: #c6ccd2;
    cursor: pointer;
  }

  input[type=range]::-moz-range-progress {
    height: 0px;
  }

  select {
    height: 30px;
    padding-left: 4px;
  }

  #app {
    text-align: center;
    color: #2c3e50;
    background-color: white;
  }

  body ::placeholder {
    padding-top: 0px;
    text-transform: none;
    font-weight: normal;
    color: #707f8f;
  }

  .navbar-container {
    padding: 10px 15px 5px 15px;
    background-color: #133353;
    height: 60px;
  }

  .interactive-tour-window-background {
    position: fixed;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    z-index: 1000;
    background-color: #333;
    opacity: 0;
    transition: opacity 150ms linear;
    display: none;
  }

  .interactive-tour-window-background.is-visible {
    display: block;
    opacity: 0.5;
  }

  .control-bar-lower-container {
    margin: 0px;
  }

  .below-chart-content {
    display: flex;
    padding: 6px 0px 0px 0px;
    background-color: white;
  }

  .screen-size-tablet .below-chart-content,
  .screen-size-small-tablet .below-chart-content,
  .screen-size-phone .below-chart-content {
    flex-direction: column;
  }

  .freestar-footer-ad-container {
    padding: 0px 0px 6px 0px;
    background-color: #f2f5f7;
  }

  .screen-size-tablet .mode-specific-ui-container,
  .screen-size-small-tablet .mode-specific-ui-container,
  .screen-size-phone .mode-specific-ui-container {
    width: 100% !important;
  }

  .screen-size-tablet .mode-buttons-container,
  .screen-size-small-tablet .mode-buttons-container,
  .screen-size-phone .mode-buttons-container {
    display: flex !important;
    width: unset !important;
    align-items: center;
    overflow-x: scroll;
    padding-bottom: 6px;
  }

  .screen-size-tablet .mode-buttons-container .mode-button-container,
  .screen-size-small-tablet .mode-buttons-container .mode-button-container,
  .screen-size-phone .mode-buttons-container .mode-button-container {
    margin: 4px 6px !important;
  }

  .screen-size-tablet .mode-buttons-container .mode-button,
  .screen-size-small-tablet .mode-buttons-container .mode-button,
  .screen-size-phone .mode-buttons-container .mode-button {
    padding-right: 10px !important;
  }

  .screen-size-tablet .navbar-container,
  .screen-size-small-tablet .navbar-container,
  .screen-size-phone .navbar-container {
    padding: 10px 10px 5px 10px;
    height: 105px;
  }

  .mode-buttons-container {
    display: inline-block;
    width: var(--mode-buttons-container-width);
    vertical-align: top;
    height: fit-content;
  }

  .mode-specific-ui-container {
    display: inline-block;
    width: calc(100% - var(--mode-buttons-container-width));
    vertical-align: top;
    padding: 0px 6px 0px 0px;
  }

  label.sc3-toggle-button {
    display: inline-block;
    position: relative;
    z-index: 1;
    background-color: white;
    border: 2px solid #c0ced8;
    border-left-width: 1px;
    border-right-width: 1px;
    color: #707f8f;
    margin: 0px;
    padding: 7px 10px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.0;
    vertical-align: middle;
    cursor: pointer;
  }

  label.sc3-toggle-button.sc3-toggle-button-form-styled {
    padding-top: 13.5px;
    padding-bottom: 13.5px;
  }

  label.sc3-toggle-button-first {
    border-radius: 3px 0px 0px 3px;
    border-left-width: 2px;
  }

  label.sc3-toggle-button-last {
    border-radius: 0px 3px 3px 0px;
    border-right-width: 2px;
  }

  label.sc3-toggle-button:not(.sc3-toggle-button-disabled):hover {
    background-color: #e2e5e9;
  }

  label.sc3-toggle-button-left-adjacent-active {
    border-left-width: 0px;
    padding-left: 11px;
  }

  label.sc3-toggle-button-right-adjacent-active {
    border-right-width: 0px;
    padding-right: 11px;
  }

  label.sc3-toggle-button-active.sc3-toggle-button-disabled,
  label.sc3-toggle-button-active:not(.sc3-toggle-button-disabled),
  label.sc3-toggle-button-active.sc3-toggle-button-disabled:hover,
  label.sc3-toggle-button-active:not(.sc3-toggle-button-disabled):hover {
    background-color: #90aabb;
    border-color: #90aabb;
    color: white;
    border-left-width: 2px;
    border-right-width: 2px;
  }

  label.sc3-toggle-button input[type=radio] {
    position: absolute;
    z-index: 0;
    clip: rect(0, 0, 0, 0);
    pointer-events: none;
    margin: 4px 0px 0px;
    padding: 0px;
    line-height: normal;
  }

  hr.sc3-form-divider {
    margin: 15px 0px;
    border: none;
    border-top: 1px solid #c0ced8;
    width: 100%;
  }

  label.sc3-form-label,
  legend.sc3-form-label {
    display: block;
    color: #4f5964;
    font-size: 12px;
    font-weight: 900;
    text-align: left;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }

  label.sc3-form-label.sc3-form-label-inline,
  legend.sc3-form-label.sc3-form-label-inline {
    display: inline-block;
    margin: 0px 6px 0px 2px;
  }

  label.sc3-form-label.sc3-form-label-small,
  legend.sc3-form-label.sc3-form-label-small {
    font-size: 10px;
  }

  fieldset {
    border: none;
    padding: 0px;
    margin: 0px;
    text-align: left;
  }

  select.sc3-select-menu,
  input[type=text].sc3-text-input,
  input[type=email].sc3-text-input,
  input[type=password].sc3-text-input,
  input[type=date].sc3-text-input,
  div.sc3-text-input,
  textarea.sc3-textarea {
    display: block;
    font-size: 16px;
    font-weight: 700;
    width: 100%;
    padding: 6px 10px;
    margin: 0px 0px 15px 0px;
    transition: all 0.35s ease;
    color: #707f8f;
  }

  select.sc3-select-menu:not(:disabled):hover,
  select.sc3-select-menu:not(:disabled):active,
  select.sc3-select-menu:not(:disabled):focus,
  input[type=text].sc3-text-input:not(.sc3-text-input-readonly):not(:disabled):hover,
  input[type=text].sc3-text-input:not(.sc3-text-input-readonly):not(:disabled):active,
  input[type=text].sc3-text-input:not(.sc3-text-input-readonly):not(:disabled):focus,
  input[type=email].sc3-text-input:not(.sc3-text-input-readonly):not(:disabled):hover,
  input[type=email].sc3-text-input:not(.sc3-text-input-readonly):not(:disabled):active,
  input[type=email].sc3-text-input:not(.sc3-text-input-readonly):not(:disabled):focus,
  input[type=password].sc3-text-input:not(.sc3-text-input-readonly):not(:disabled):hover,
  input[type=password].sc3-text-input:not(.sc3-text-input-readonly):not(:disabled):active,
  input[type=password].sc3-text-input:not(.sc3-text-input-readonly):not(:disabled):focus,
  input[type=date].sc3-text-input:not(.sc3-text-input-readonly):not(:disabled):hover,
  input[type=date].sc3-text-input:not(.sc3-text-input-readonly):not(:disabled):active,
  input[type=date].sc3-text-input:not(.sc3-text-input-readonly):not(:disabled):focus,
  textarea.sc3-textarea:not(:disabled):hover,
  textarea.sc3-textarea:not(:disabled):active,
  textarea.sc3-textarea:not(:disabled):focus,
  div.sc3-text-input:not(.sc3-text-input-readonly):hover,
  div.sc3-text-input:not(.sc3-text-input-readonly):active,
  div.sc3-text-input:not(.sc3-text-input-readonly):focus {
    border-color: #707f8f;
    color: #2d3339;
  }

  select.sc3-select-menu.sc3-select-menu-inline,
  input[type=text].sc3-text-input.sc3-text-input-inline,
  input[type=email].sc3-text-input.sc3-text-input-inline,
  input[type=password].sc3-text-input.sc3-text-input-inline,
  input[type=date].sc3-text-input.sc3-text-input-inline,
  div.sc3-text-input.sc3-text-input-inline {
    display: inline-block;
    vertical-align: top;
    width: unset;
    font-size: 12px;
  }

  select.sc3-select-menu,
  input[type=text].sc3-text-input,
  input[type=email].sc3-text-input,
  input[type=password].sc3-text-input,
  input[type=date].sc3-text-input,
  div.sc3-text-input {
    height: 45px;
  }

  select.sc3-select-menu.sc3-select-menu-inline,
  input[type=text].sc3-text-input.sc3-text-input-inline,
  input[type=email].sc3-text-input.sc3-text-input-inline,
  input[type=password].sc3-text-input.sc3-text-input-inline,
  input[type=date].sc3-text-input.sc3-text-input-inline,
  div.sc3-text-input.sc3-text-input-inline,
  button.sc3-button.sc3-button-inline,
  a.sc3-button.sc3-button-inline {
    height: 30px;
    padding: 0px 0px 0px 4px;
    margin: 0px 4px 0px 0px;
  }

  button.sc3-button.sc3-button-inline,
  a.sc3-button.sc3-button-inline {
    padding: 0px 10px;
  }

  select.sc3-select-menu.sc3-select-menu-inline.sc3-select-menu-full-width,
  input[type=text].sc3-text-input.sc3-text-input-inline.sc3-text-input-full-width,
  input[type=email].sc3-text-input.sc3-text-input-inline.sc3-text-input-full-width,
  input[type=password].sc3-text-input.sc3-text-input-inline.sc3-text-input-full-width,
  input[type=date].sc3-text-input.sc3-text-input-inline.sc3-text-input-full-width,
  div.sc3-text-input.sc3-text-input-inline.sc3-text-input-full-width {
    width: 100%;
    margin-right: 0px;
  }

  input[type=text].sc3-text-input.sc3-text-input-invalid:not(.sc3-text-input-readonly):not(:disabled),
  input[type=email].sc3-text-input.sc3-text-input-invalid:not(.sc3-text-input-readonly):not(:disabled),
  input[type=password].sc3-text-input.sc3-text-input-invalid:not(.sc3-text-input-readonly):not(:disabled),
  input[type=date].sc3-text-input.sc3-text-input-invalid:not(.sc3-text-input-readonly):not(:disabled),
  div.sc3-text-input.sc3-text-input-invalid:not(.sc3-text-input-readonly):not(:disabled) {
    border-color: red;
  }

  input[type=text].sc3-text-input.sc3-text-input-slim,
  input[type=email].sc3-text-input.sc3-text-input-slim,
  input[type=password].sc3-text-input.sc3-text-input-slim,
  input[type=date].sc3-text-input.sc3-text-input-slim,
  div.sc3-text-input.sc3-text-input-slim {
    width: 66px;
  }

  input[type=text].sc3-text-input.sc3-text-input-semi-slim,
  input[type=email].sc3-text-input.sc3-text-input-semi-slim,
  input[type=password].sc3-text-input.sc3-text-input-semi-slim,
  input[type=date].sc3-text-input.sc3-text-input-semi-slim,
  div.sc3-text-input.sc3-text-input-semi-slim {
    width: 120px;
  }

  select.sc3-select-menu.sc3-select-menu-inline {
    padding-right: 20px;
  }

  select.sc3-select-menu {
    background: #fff url(//d.stockcharts.com/img/menu-icon.png) no-repeat right 6px center;
    background-size: 10px 12px;
    -o-appearance: none;
    -ms-appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    padding: 5px 25px 5px 10px;
    line-height: 1.5;
    text-overflow: ellipsis;
  }

  select.sc3-select-menu.sc3-select-menu-compact,
  input[type=text].sc3-text-input.sc3-text-input-compact,
  input[type=email].sc3-text-input.sc3-text-input-compact,
  input[type=password].sc3-text-input.sc3-text-input-compact,
  input[type=date].sc3-text-input.sc3-text-input-compact {
    border-width: 1px;
    border-style: solid;
    height: var(--sc3-compact-height);
  }

  select.sc3-select-menu.sc3-select-menu-no-margins,
  input[type=text].sc3-text-input.sc3-text-input-no-margins,
  input[type=email].sc3-text-input.sc3-text-input-no-margins,
  input[type=password].sc3-text-input.sc3-text-input-no-margins,
  input[type=date].sc3-text-input.sc3-text-input-no-margins {
    margin: 0px;
  }

  select.sc3-select-menu.sc3-select-menu-white-and-grey,
  select.sc3-select-menu.sc3-select-menu-white-and-grey:hover,
  select.sc3-select-menu.sc3-select-menu-white-and-grey:active,
  select.sc3-select-menu.sc3-select-menu-white-and-grey:focus,
  input[type=text].sc3-text-input.sc3-text-input-grey-and-black,
  input[type=text].sc3-text-input.sc3-text-input-grey-and-black:hover,
  input[type=text].sc3-text-input.sc3-text-input-grey-and-black:active,
  input[type=text].sc3-text-input.sc3-text-input-grey-and-black:focus,
  input[type=email].sc3-text-input.sc3-text-input-grey-and-black,
  input[type=email].sc3-text-input.sc3-text-input-grey-and-black:hover,
  input[type=email].sc3-text-input.sc3-text-input-grey-and-black:active,
  input[type=email].sc3-text-input.sc3-text-input-grey-and-black:focus,
  input[type=password].sc3-text-input.sc3-text-input-grey-and-black,
  input[type=password].sc3-text-input.sc3-text-input-grey-and-black:hover,
  input[type=password].sc3-text-input.sc3-text-input-grey-and-black:active,
  input[type=password].sc3-text-input.sc3-text-input-grey-and-black:focus,
  input[type=date].sc3-text-input.sc3-text-input-grey-and-black,
  input[type=date].sc3-text-input.sc3-text-input-grey-and-black:hover,
  input[type=date].sc3-text-input.sc3-text-input-grey-and-black:active,
  input[type=date].sc3-text-input.sc3-text-input-grey-and-black:focus {
    color: #2d3339;
    background-color: white;
    border-color: #8d99a5;
    outline: 2px solid transparent;
  }

  select.sc3-select-menu.sc3-select-menu-white-and-grey:not(:disabled):hover,
  select.sc3-select-menu.sc3-select-menu-white-and-grey:not(:disabled):active,
  select.sc3-select-menu.sc3-select-menu-white-and-grey:not(:disabled):focus,
  input[type=text].sc3-text-input.sc3-text-input-grey-and-black:not(.sc3-text-input-readonly):not(:disabled):hover,
  input[type=text].sc3-text-input.sc3-text-input-grey-and-black:not(.sc3-text-input-readonly):not(:disabled):active,
  input[type=text].sc3-text-input.sc3-text-input-grey-and-black:not(.sc3-text-input-readonly):not(:disabled):focus,
  input[type=email].sc3-text-input.sc3-text-input-grey-and-black:not(.sc3-text-input-readonly):not(:disabled):hover,
  input[type=email].sc3-text-input.sc3-text-input-grey-and-black:not(.sc3-text-input-readonly):not(:disabled):active,
  input[type=email].sc3-text-input.sc3-text-input-grey-and-black:not(.sc3-text-input-readonly):not(:disabled):focus,
  input[type=password].sc3-text-input.sc3-text-input-grey-and-black:not(.sc3-text-input-readonly):not(:disabled):hover,
  input[type=password].sc3-text-input.sc3-text-input-grey-and-black:not(.sc3-text-input-readonly):not(:disabled):active,
  input[type=password].sc3-text-input.sc3-text-input-grey-and-black:not(.sc3-text-input-readonly):not(:disabled):focus,
  input[type=date].sc3-text-input.sc3-text-input-grey-and-black:not(.sc3-text-input-readonly):not(:disabled):hover,
  input[type=date].sc3-text-input.sc3-text-input-grey-and-black:not(.sc3-text-input-readonly):not(:disabled):active,
  input[type=date].sc3-text-input.sc3-text-input-grey-and-black:not(.sc3-text-input-readonly):not(:disabled):focus {
    border-color: #3281d0;
    outline: 2px solid #3281d0;
  }

  textarea.sc3-textarea {
    min-height: 75px;
    resize: none;
    padding: 10px;
  }

  textarea.sc3-textarea.sc3-textarea-resizeable {
    resize: vertical;
    transition: all 0.35s ease, height 0ms linear;
  }

  div.sc3-form-checkbox-container {
    text-align: left;
    margin: 0px 0px 15px 0px;
    color: #707f8f;
  }

  button.sc3-button,
  a.sc3-button {
    border-width: 2px;
    border-style: solid;
    border-radius: 3px;
    display: inline-block;
    margin: 5px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 700;
    vertical-align: middle;
    white-space: nowrap;
    text-decoration: none;
    transition: all 0.35s ease;
    touch-action: manipulation;
    user-select: none;
  }

  button.sc3-button-green,
  a.sc3-button-green {
    background-color: #219155;
    border-color: #219155;
    color: white;
  }

  button.sc3-button-green:not(:disabled):hover,
  button.sc3-button-green:not(:disabled):active,
  button.sc3-button-green:not(:disabled):focus,
  button.sc3-button-green:not(:disabled).sc3-button-active,
  a.sc3-button-green:not(:disabled):hover,
  a.sc3-button-green:not(:disabled):active,
  a.sc3-button-green:not(:disabled):focus,
  a.sc3-button-green:not(:disabled).sc3-button-active {
    background-color: #26a65b;
    border-color: #26a65b;
  }

  button.sc3-button-grey,
  a.sc3-button-grey {
    background-color: #707f8f;
    border-color: #707f8f;
    color: white;
  }

  button.sc3-button-grey:not(:disabled):hover,
  button.sc3-button-grey:not(:disabled):active,
  button.sc3-button-grey:not(:disabled):focus,
  button.sc3-button-grey:not(:disabled).sc3-button-active,
  a.sc3-button-grey:not(:disabled):hover,
  a.sc3-button-grey:not(:disabled):active,
  a.sc3-button-grey:not(:disabled):focus,
  a.sc3-button-grey:not(:disabled).sc3-button-active {
    background-color: #8d99a5;
    border-color: #8d99a5;
  }

  button.sc3-button-light-grey,
  a.sc3-button-light-grey {
    background-color: #a9b2bc;
    border-color: #a9b2bc;
    color: white;
  }

  button.sc3-button-light-grey:not(:disabled):hover,
  button.sc3-button-light-grey:not(:disabled):active,
  button.sc3-button-light-grey:not(:disabled):focus,
  button.sc3-button-light-grey:not(:disabled).sc3-button-active,
  a.sc3-button-light-grey:not(:disabled):hover,
  a.sc3-button-light-grey:not(:disabled):active,
  a.sc3-button-light-grey:not(:disabled):focus,
  a.sc3-button-light-grey:not(:disabled).sc3-button-active {
    background-color: #707f8f;
    border-color: #707f8f;
  }

  button.sc3-button-orange,
  a.sc3-button-orange {
    background-color: #f9690e;
    border-color: #f9690e;
    color: white;
  }

  button.sc3-button-orange:not(:disabled):hover,
  button.sc3-button-orange:not(:disabled):active,
  button.sc3-button-orange:not(:disabled):focus,
  button.sc3-button-orange:not(:disabled).sc3-button-active,
  a.sc3-button-orange:not(:disabled):hover,
  a.sc3-button-orange:not(:disabled):active,
  a.sc3-button-orange:not(:disabled):focus,
  a.sc3-button-orange:not(:disabled).sc3-button-active {
    background-color: #fa8238;
    border-color: #fa8238;
  }

  button.sc3-button-red,
  a.sc3-button-red {
    background-color: #cc000e;
    border-color: #cc000e;
    color: white;
  }

  button.sc3-button-red:not(:disabled):hover,
  button.sc3-button-red:not(:disabled):active,
  button.sc3-button-red:not(:disabled):focus,
  button.sc3-button-red:not(:disabled).sc3-button-active,
  a.sc3-button-red:not(:disabled):hover,
  a.sc3-button-red:not(:disabled):active,
  a.sc3-button-red:not(:disabled):focus,
  a.sc3-button-red:not(:disabled).sc3-button-active {
    background-color: #e6000f;
    border-color: #e6000f;
  }

  button.sc3-button-light-green,
  a.sc3-button-light-green {
    background-color: #2abb67;
    border-color: #2abb67;
    color: white;
  }

  button.sc3-button-light-green:not(:disabled):hover,
  button.sc3-button-light-green:not(:disabled):active,
  button.sc3-button-light-green:not(:disabled):focus,
  button.sc3-button-light-green:not(:disabled).sc3-button-active,
  a.sc3-button-light-green:not(:disabled):hover,
  a.sc3-button-light-green:not(:disabled):active,
  a.sc3-button-light-green:not(:disabled):focus,
  a.sc3-button-light-green:not(:disabled).sc3-button-active {
    background-color: #26a65b;
    border-color: #26a65b;
  }

  button.sc3-button-blue,
  a.sc3-button-blue {
    background-color: #0f4d8a;
    border-color: #0f4d8a;
    color: white;
  }

  button.sc3-button-blue:not(:disabled):hover,
  button.sc3-button-blue:not(:disabled):active,
  button.sc3-button-blue:not(:disabled):focus,
  button.sc3-button-blue:not(:disabled).sc3-button-active,
  a.sc3-button-blue:not(:disabled):hover,
  a.sc3-button-blue:not(:disabled):active,
  a.sc3-button-blue:not(:disabled):focus,
  a.sc3-button-blue:not(:disabled).sc3-button-active {
    background-color: #3281d0;
    border-color: #3281d0;
  }

  button.sc3-button-light-blue,
  a.sc3-button-light-blue {
    background-color: #3281d0;
    border-color: #3281d0;
    color: white;
  }

  button.sc3-button-light-blue:not(:disabled):hover,
  button.sc3-button-light-blue:not(:disabled):active,
  button.sc3-button-light-blue:not(:disabled):focus,
  button.sc3-button-light-blue:not(:disabled).sc3-button-active,
  a.sc3-button-light-blue:not(:disabled):hover,
  a.sc3-button-light-blue:not(:disabled):active,
  a.sc3-button-light-blue:not(:disabled):focus,
  a.sc3-button-light-blue:not(:disabled).sc3-button-active {
    background-color: #1282f3;
    border-color: #1282f3;
  }

  button.sc3-button-white-and-dark-grey,
  a.sc3-button-white-and-dark-grey {
    border-width: 1px;
    background-color: white;
    border-color: #c0ced8;
    color: #2d3339;
  }

  button.sc3-button-white-and-dark-grey:not(:disabled):hover,
  button.sc3-button-white-and-dark-grey:not(:disabled):active,
  button.sc3-button-white-and-dark-grey:not(:disabled):focus,
  button.sc3-button-white-and-dark-grey:not(:disabled).sc3-button-active,
  a.sc3-button-white-and-dark-grey:not(:disabled):hover,
  a.sc3-button-white-and-dark-grey:not(:disabled):active,
  a.sc3-button-white-and-dark-grey:not(:disabled):focus,
  a.sc3-button-white-and-dark-grey:not(:disabled).sc3-button-active {
    background-color: #d0dbe2;
    border-color: #c0ced8;
    color: #16191d;
  }

  button.sc3-button-white-and-light-grey,
  a.sc3-button-white-and-light-grey {
    background-color: white;
    border-color: #c0ced8;
    color: #707f8f;
  }

  button.sc3-button-white-and-light-grey:not(:disabled):hover,
  button.sc3-button-white-and-light-grey:not(:disabled):active,
  button.sc3-button-white-and-light-grey:not(:disabled):focus,
  button.sc3-button-white-and-light-grey:not(:disabled).sc3-button-active,
  a.sc3-button-white-and-light-grey:not(:disabled):hover,
  a.sc3-button-white-and-light-grey:not(:disabled):active,
  a.sc3-button-white-and-light-grey:not(:disabled):focus,
  a.sc3-button-white-and-light-grey:not(:disabled).sc3-button-active {
    border-color: #707f8f;
    color: #2d3339;
  }

  button.sc3-button-transparent-and-very-light-grey,
  a.sc3-button-transparent-and-very-light-grey {
    border-width: 1px;
    background-color: transparent;
    border-color: #c0ced8;
    color: #707f8f;
  }

  button.sc3-button-transparent-and-very-light-grey:not(:disabled):hover,
  button.sc3-button-transparent-and-very-light-grey:not(:disabled):active,
  button.sc3-button-transparent-and-very-light-grey:not(:disabled):focus,
  button.sc3-button-transparent-and-very-light-grey:not(:disabled).sc3-button-active,
  a.sc3-button-transparent-and-very-light-grey:not(:disabled):hover,
  a.sc3-button-transparent-and-very-light-grey:not(:disabled):active,
  a.sc3-button-transparent-and-very-light-grey:not(:disabled):focus,
  a.sc3-button-transparent-and-very-light-grey:not(:disabled).sc3-button-active {
    background-color: #d0dbe2;
    border-color: #c0ced8;
    color: #16191d;
  }

  button.sc3-button-transparent-and-grey,
  a.sc3-button-transparent-and-grey {
    background-color: transparent;
    border-color: #a9b2bc;
    color: #a9b2bc;
  }

  button.sc3-button-transparent-and-grey:not(:disabled):hover,
  button.sc3-button-transparent-and-grey:not(:disabled):active,
  button.sc3-button-transparent-and-grey:not(:disabled):focus,
  button.sc3-button-transparent-and-grey:not(:disabled).sc3-button-active,
  a.sc3-button-transparent-and-grey:not(:disabled):hover,
  a.sc3-button-transparent-and-grey:not(:disabled):active,
  a.sc3-button-transparent-and-grey:not(:disabled):focus,
  a.sc3-button-transparent-and-grey:not(:disabled).sc3-button-active {
    background-color: #707f8f;
    border-color: #707f8f;
    color: white;
  }

  button.sc3-button-transparent-and-dark-grey,
  a.sc3-button-transparent-and-dark-grey {
    background-color: transparent;
    border-color: #2d3339;
    color: #2d3339;
  }

  button.sc3-button-transparent-and-dark-grey:not(:disabled):hover,
  button.sc3-button-transparent-and-dark-grey:not(:disabled):active,
  button.sc3-button-transparent-and-dark-grey:not(:disabled):focus,
  button.sc3-button-transparent-and-dark-grey:not(:disabled).sc3-button-active,
  a.sc3-button-transparent-and-dark-grey:not(:disabled):hover,
  a.sc3-button-transparent-and-dark-grey:not(:disabled):active,
  a.sc3-button-transparent-and-dark-grey:not(:disabled):focus,
  a.sc3-button-transparent-and-dark-grey:not(:disabled).sc3-button-active {
    background-color: #2d3339;
    border-color: #2d3339;
    color: white;
  }

  button.sc3-button-transparent,
  a.sc3-button-transparent {
    border-width: 1px;
    background-color: transparent;
    border-color: #c0ced8;
    color: #d0dbe2;
  }

  button.sc3-button-transparent:not(:disabled):hover,
  button.sc3-button-transparent:not(:disabled):active,
  button.sc3-button-transparent:not(:disabled):focus,
  button.sc3-button-transparent:not(:disabled).sc3-button-active,
  a.sc3-button-transparent:not(:disabled):hover,
  a.sc3-button-transparent:not(:disabled):active,
  a.sc3-button-transparent:not(:disabled):focus,
  a.sc3-button-transparent:not(:disabled).sc3-button-active {
    background-color: #3281d0;
    border-color: #3281d0;
    color: white;
  }

  button.sc3-button-transparent-and-light-green,
  a.sc3-button-transparent-and-light-green {
    background-color: transparent;
    border-color: #2abb67;
    color: #2abb67;
  }

  button.sc3-button-transparent-and-light-green:not(:disabled):hover,
  button.sc3-button-transparent-and-light-green:not(:disabled):active,
  button.sc3-button-transparent-and-light-green:not(:disabled):focus,
  button.sc3-button-transparent-and-light-green:not(:disabled).sc3-button-active,
  a.sc3-button-transparent-and-light-green:not(:disabled):hover,
  a.sc3-button-transparent-and-light-green:not(:disabled):active,
  a.sc3-button-transparent-and-light-green:not(:disabled):focus,
  a.sc3-button-transparent-and-light-green:not(:disabled).sc3-button-active {
    background-color: #26a65b;
    border-color: #26a65b;
    color: white;
  }

  button.sc3-button-link,
  a.sc3-button-link {
    margin: 5px;
    padding: 0px;
    font-size: 14px;
    vertical-align: middle;
    background-color: transparent;
    border: none;
    font-weight: 700;
    white-space: nowrap;
    text-decoration: none;
    text-align: center;
    color: #0f4d8a;
    transition: all 0.35s ease;
    touch-action: manipulation;
    user-select: none;
  }

  button.sc3-button-link.sc3-button-link-inline,
  a.sc3-button-link.sc3-button-link-inline {
    margin: 0px;
    vertical-align: baseline;
  }

  button.sc3-button-link:not(:disabled):hover,
  button.sc3-button-link:not(:disabled):active,
  button.sc3-button-link:not(:disabled):focus,
  button.sc3-button-link:not(:disabled).sc3-button-active,
  a.sc3-button-link:not(:disabled):hover,
  a.sc3-button-link:not(:disabled):active,
  a.sc3-button-link:not(:disabled):focus,
  a.sc3-button-link:not(:disabled).sc3-button-active {
    color: #3281d0;
  }

  button.sc3-button-link-dark-grey,
  a.sc3-button-link-dark-grey {
    color: #2d3339;
  }

  button.sc3-button-link-dark-grey:not(:disabled):hover,
  button.sc3-button-link-dark-grey:not(:disabled):active,
  button.sc3-button-link-dark-grey:not(:disabled):focus,
  button.sc3-button-link-dark-grey:not(:disabled).sc3-button-active,
  a.sc3-button-link-dark-grey:not(:disabled):hover,
  a.sc3-button-link-dark-grey:not(:disabled):active,
  a.sc3-button-link-dark-grey:not(:disabled):focus,
  a.sc3-button-link-dark-grey:not(:disabled).sc3-button-active {
    color: #5a6672;
  }

  button.sc3-button-link-light-blue,
  a.sc3-button-link-light-blue {
    color: #1282f3;
  }

  button.sc3-button-link-light-blue:not(:disabled):hover,
  button.sc3-button-link-light-blue:not(:disabled):active,
  button.sc3-button-link-light-blue:not(:disabled):focus,
  button.sc3-button-link-light-blue:not(:disabled).sc3-button-active,
  a.sc3-button-link-light-blue:not(:disabled):hover,
  a.sc3-button-link-light-blue:not(:disabled):active,
  a.sc3-button-link-light-blue:not(:disabled):focus,
  a.sc3-button-link-light-blue:not(:disabled).sc3-button-active {
    color: #3281d0;
  }

  button.sc3-button-link-green,
  a.sc3-button-link-green {
    color: #2abb67;
  }

  button.sc3-button-link-green:not(:disabled):hover,
  button.sc3-button-link-green:not(:disabled):active,
  button.sc3-button-link-green:not(:disabled):focus,
  button.sc3-button-link-green:not(:disabled).sc3-button-active,
  a.sc3-button-link-green:not(:disabled):hover,
  a.sc3-button-link-green:not(:disabled):active,
  a.sc3-button-link-green:not(:disabled):focus,
  a.sc3-button-link-green:not(:disabled).sc3-button-active {
    color: #26a65b;
  }

  button.sc3-button-extra-large,
  a.sc3-button-extra-large {
    margin: 12px;
    padding: 12px 20px;
    font-size: 16px;
  }

  button.sc3-button-medium,
  a.sc3-button-medium {
    padding: 6px 10px;
    font-size: 12px;
  }

  button.sc3-button-small,
  a.sc3-button-small {
    padding: 4px 8px;
    font-size: 12px;
  }

  button.sc3-button-extra-small,
  a.sc3-button-extra-small {
    padding: 3px 5px;
    font-size: 10px;
  }

  button.sc3-button.sc3-button-fixed-height,
  a.sc3-button.sc3-button-fixed-height {
    margin: 0px;
    height: var(--sc3-fixed-height);
    padding: 0px 6px;
    font-size: 11px;
  }

  button.sc3-button.sc3-button-fixed-height.icon-large i {
    font-size: 14px;
  }

  button.sc3-button.sc3-button-fixed-height.icon-large span,
  a.sc3-button.sc3-button-fixed-height.icon-large span {
    height: 14px;
  }

  button.sc3-button.sc3-button-fixed-height.icon-large i,
  button.sc3-button.sc3-button-fixed-height.icon-large span,
  a.sc3-button.sc3-button-fixed-height.icon-large span {
    display: inline-block;
    vertical-align: middle;
  }

  button.sc3-button.sc3-button-last,
  a.sc3-button.sc3-button-last {
    margin-right: 0px;
  }

  button.sc3-button.sc3-button-small-with-fixed-height,
  a.sc3-button.sc3-button-small-with-fixed-height {
    margin: 0px;
    height: var(--sc3-fixed-height);
    padding: 0px 6px;
  }

  button.sc3-button.sc3-button-control-bar-lower-size,
  a.sc3-button.sc3-button-control-bar-lower-size {
    height: 26px;
    padding: 3px 6px;
    margin: 0px;
  }

  button.sc3-button.sc3-button-square-medium-height,
  a.sc3-button.sc3-button-square-medium-height {
    height: var(--sc3-compact-height);
    width: var(--sc3-compact-height);
    padding: 0px;
    margin: 0px;
  }

  a.sc3-button.sc3-button-square-medium-height {
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  button.sc3-button.sc3-button-top-aligned,
  a.sc3-button.sc3-button-top-aligned {
    vertical-align: top;
  }

  button.sc3-button.sc3-button-bottom-aligned,
  a.sc3-button.sc3-button-bottom-aligned {
    vertical-align: bottom;
  }

  button:disabled,
  a:disabled,
  a[disabled=true],
  a[disabled=disabled],
  select:disabled,
  input:disabled,
  textarea:disabled,
  label.sc3-toggle-button-disabled {
    opacity: 0.8 !important;
    cursor: not-allowed !important;
  }

  a:disabled,
  a[disabled=true],
  a[disabled=disabled] {
    pointer-events: none;
  }

  i.fa-bell-plus {
    font-size: calc(1em + 2px);
    width: calc(1.25em - 2.5px);
  }

  button.dropdown-menu-toggle-arrow-icon-button,
  button.sc3-button.dropdown-menu-toggle-arrow-icon-button {
    position: relative;
    padding-right: 1.75em;
    text-align: left;
  }

  button.dropdown-menu-toggle-arrow-icon-button i.fa-angle-down {
    transform: rotate(0deg);
    transition: transform 250ms ease;
    position: absolute;
    right: 8px;
    top: 0px;
    bottom: 0px;
    margin-top: auto;
    margin-bottom: auto;
    height: fit-content;
  }

  button.dropdown-menu-toggle-arrow-icon-button.dropdown-menu-toggle-arrow-icon-button-open i.fa-angle-down {
    transform: rotate(-180deg);
  }

  button.sc3-button-content-visibility-sliding-toggle i.fa-caret-right {
    transform: rotate(0deg);
    transition: transform 250ms ease;
  }

  button.sc3-button-content-visibility-sliding-toggle.sc3-button-content-visibility-sliding-toggle-open i.fa-caret-right {
    transform: rotate(90deg);
  }

  ul.dropdown-menu,
  div.dropdown-menu {
    max-height: none;
    padding: 3px 0px;
    border: 1px solid #c0ced8;
    opacity: 1.0;
    overflow-y: auto;
    overflow-x: hidden;
    transition: opacity 0.3s, max-height 0.6s;
    position: absolute;
    left: 0px;
    display: none;
    margin: 0px;
    list-style: none;
    background-color: white;
    background-clip: padding-box;
    border-radius: 4px;
    box-shadow: 0 6px 12px rgb(0 0 0 / 18%);
  }

  ul.dropdown-menu:not(.dropdown-menu-slim),
  div.dropdown-menu:not(.dropdown-menu-slim) {
    min-width: 160px;
  }

  div.dropdown-menu {
    padding: 8px;
  }

  ul.dropdown-menu-open,
  div.dropdown-menu-open {
    display: block;
    z-index: 998;
  }

  ul.dropdown-menu-right-aligned,
  div.dropdown-menu-right-aligned {
    left: unset;
    right: 0px;
  }

  ul.custom-right-click-dropdown-menu,
  div.custom-right-click-dropdown-menu {
    position: absolute;
    top: unset;
    right: unset;
    bottom: unset;
    left: unset;
  }

  li.dropdown-menu-option {
    text-align: left;
    font-size: 0px;
    line-height: 23px;
    white-space: nowrap;
    position: relative;
  }

  li.disabled {
    cursor: not-allowed;
  }

  li.dropdown-menu-option.dropdown-menu-option-verbose button.dropdown-menu-option-button,
  li.dropdown-menu-option.dropdown-menu-option-verbose a.dropdown-menu-option-button {
    font-size: 16px;
    font-weight: 900;
    color: #2d3339;
  }

  li.dropdown-menu-option.dropdown-menu-option-verbose button.dropdown-menu-option-button span.dropdown-menu-option-verbose-description,
  li.dropdown-menu-option.dropdown-menu-option-verbose a.dropdown-menu-option-button span.dropdown-menu-option-verbose-description {
    color: #707f8f;
    margin-left: 25px;
    font-size: 12px;
    font-weight: normal;
    white-space: nowrap;
  }

  button.dropdown-menu-option-button,
  a.dropdown-menu-option-button {
    display: inline-block;
    vertical-align: middle;
    padding: 2px 10px;
    margin: 0px;
    font-size: 14px;
    font-weight: 700;
    color: #5a6672;
    background-color: transparent;
    border: none;
    border-radius: 0px;
    width: 100%;
    text-align: left;
    white-space: nowrap;
    text-decoration: none;
    transition: all 0.35s ease;
    line-height: inherit;
  }

  button.dropdown-menu-option-button i,
  a.dropdown-menu-option-button i {
    margin-right: 2px;
    line-height: 1.4;
  }

  button.dropdown-menu-option-button i.custom-scc-icon,
  a.dropdown-menu-option-button i.custom-scc-icon {
    vertical-align: middle;
  }

  li.dropdown-menu-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 16px;
    font-weight: 900;
    color: #707f8f;
    text-align: left;
    padding: 2px 10px;
    margin: 0px;
  }

  li.dropdown-menu-option-divider {
    margin: 3px 0px;
    background-color: #c0ced8;
    height: 1px;
  }

  button.dropdown-menu-option-star-button,
  a.button.dropdown-menu-option-star-button {
    font-weight: 400;
  }

  li.dropdown-menu-option:hover .dropdown-menu-option-button:not(:disabled) {
    background-color: #dfe7ec;
    color: #16191d;
  }

  button.dropdown-menu-option-icon-button-filled i.yellow-icon,
  button:hover i.yellow-icon,
  a.dropdown-menu-option-icon-button-filled i.yellow-icon,
  a:hover i.yellow-icon {
    color: #f6c709;
  }

  button.dropdown-menu-option-icon-button-filled i.orange-icon,
  button:hover i.orange-icon,
  a.dropdown-menu-option-icon-button-filled i.orange-icon,
  a:hover i.orange-icon {
    color: #fa8238;
  }

  button:hover i.blue-icon,
  a:hover i.blue-icon {
    color: #3281d0;
  }

  button:hover i.red-icon,
  a:hover i.red-icon {
    color: #cc000e;
  }

  button:hover i.dark-blue-icon,
  a:hover i.dark-blue-icon {
    color: #0f4d8a;
  }

  button:hover i.purple-icon,
  a:hover i.purple-icon {
    color: #8740a5;
  }

  button.dropdown-menu-option-icon-button-filled i,
  a.dropdown-menu-option-icon-button-filled i {
    font-weight: 900;
  }

  span.dropdown-menu-title-right-corner-ui-container {
    display: inline-block;
    margin: 0px 0px 0px 18px;
    font-size: 0px;
  }

  span.dropdown-menu-title-right-corner-ui-container a,
  span.dropdown-menu-title-right-corner-ui-container button {
    margin: 0px;
  }

  table.sc3-table {
    box-sizing: initial;
    margin: 0px;
    background-color: transparent;
    max-width: 100%;
    width: 100%;
    border-collapse: collapse;
  }

  table.sc3-table thead {
    background-color: #707f8f;
    margin: 0px;
    max-width: 100%;
    width: 100%;
  }

  table.sc3-table thead tr th {
    background-color: #707f8f;
    border-top: none;
    vertical-align: bottom;
    padding: 8px;
    white-space: nowrap;
    text-transform: uppercase;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 14px;
    font-weight: 900;
    text-align: left;
    color: white;
    border: 1px solid #c0ced8;
    border-bottom: 0;
    border-right: 0;
  }

  table.data-table thead tr th.column-group-header {
    padding: 10px;
    background-color: transparent;
    border: none;
    font-size: 20px;
    color: #707f8f;
    text-align: center;
    text-transform: none;
  }

  table.sc3-table tbody tr td {
    background-color: white;
    color: #2d3339;
    font-size: 14px;
    font-weight: 700;
    vertical-align: middle;
    border: 1px solid #c0ced8;
    border-bottom: 0;
    border-right: 0;
  }

  table.sc3-table td {
    padding: 8px;
  }

  table.sc3-table th {
    padding: 5px;
  }

  div.chart-style-button-color-option-group {
    height: 45px;
    display: flex;
    align-items: center;
  }

  label.chart-style-button-color-option {
    display: inline-block;
    cursor: pointer;
    margin: 0px 6px 0px 2px;
    font-size: 0px;
  }

  label.chart-style-button-color-option:last-of-type {
    margin-right: 0px;
  }

  label.chart-style-button-color-option input[type=radio] {
    display: none;
  }

  span.chart-style-button-color-swatch {
    display: inline-block;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    padding: 0px;
    margin: 0px;
    border: none;
    vertical-align: middle;
  }

  label.chart-style-button-color-option.selected-chart-style-button-background-color-option span.chart-style-button-color-swatch {
    outline-width: 2px;
    outline-style: solid;
    outline-offset: 2px;
  }

  label.chart-style-button-color-option.disabled-chart-style-button-background-color-option {
    cursor: not-allowed;
  }

  label.chart-style-button-color-option.disabled-chart-style-button-background-color-option span.chart-style-button-color-swatch {
    opacity: 0.4;
  }

  i.twitter-button-icon {
    color: #1da1f2;
  }

  i.facebook-button-icon {
    color: #3b5998;
  }

  i.linkedin-button-icon {
    color: #0077b5;
  }

  /* white background behind social media icons */
  i.twitter-button-icon,
  i.facebook-button-icon,
  i.linkedin-button-icon {
    background-image: linear-gradient(to bottom, transparent 20%, white 20%, white 93%, transparent 93%);
    background-size: 55%;
    background-position: 50% 0;
    background-repeat: no-repeat;
  }

  .screen-size-phone .phone-hidden {
    display: none !important;
  }

  .screen-size-small-tablet .small-tablet-hidden,
  .screen-size-phone .small-tablet-hidden {
    display: none !important;
  }

  .screen-size-tablet .tablet-hidden,
  .screen-size-small-tablet .tablet-hidden,
  .screen-size-phone .tablet-hidden {
    display: none !important;
  }

  .screen-size-extra-small-desktop .extra-small-desktop-hidden,
  .screen-size-tablet .extra-small-desktop-hidden,
  .screen-size-small-tablet .extra-small-desktop-hidden,
  .screen-size-phone .extra-small-desktop-hidden {
    display: none !important;
  }

  .screen-size-small-desktop .small-desktop-hidden,
  .screen-size-extra-small-desktop .small-desktop-hidden,
  .screen-size-tablet .small-desktop-hidden,
  .screen-size-small-tablet .small-desktop-hidden,
  .screen-size-phone .small-desktop-hidden {
    display: none !important;
  }

  .screen-size-medium-desktop .medium-desktop-hidden,
  .screen-size-small-desktop .medium-desktop-hidden,
  .screen-size-extra-small-desktop .medium-desktop-hidden,
  .screen-size-tablet .medium-desktop-hidden,
  .screen-size-small-tablet .medium-desktop-hidden,
  .screen-size-phone .medium-desktop-hidden {
    display: none !important;
  }

  .screen-size-large-desktop .large-desktop-hidden,
  .screen-size-medium-desktop .large-desktop-hidden,
  .screen-size-small-desktop .large-desktop-hidden,
  .screen-size-extra-small-desktop .large-desktop-hidden,
  .screen-size-tablet .large-desktop-hidden,
  .screen-size-small-tablet .large-desktop-hidden,
  .screen-size-phone .large-desktop-hidden {
    display: none !important;
  }

  .screen-size-extra-large-desktop .phone-show,
  .screen-size-large-desktop .phone-show,
  .screen-size-medium-desktop .phone-show,
  .screen-size-small-desktop .phone-show,
  .screen-size-extra-small-desktop .phone-show,
  .screen-size-tablet .phone-show,
  .screen-size-small-tablet .phone-show {
    display: none !important;
  }

  .screen-size-extra-large-desktop .small-tablet-show,
  .screen-size-large-desktop .small-tablet-show,
  .screen-size-medium-desktop .small-tablet-show,
  .screen-size-small-desktop .small-tablet-show,
  .screen-size-extra-small-desktop .small-tablet-show,
  .screen-size-tablet .small-tablet-show {
    display: none !important;
  }

  .screen-size-extra-large-desktop .tablet-show,
  .screen-size-large-desktop .tablet-show,
  .screen-size-medium-desktop .tablet-show,
  .screen-size-small-desktop .tablet-show,
  .screen-size-extra-small-desktop .tablet-show {
    display: none !important;
  }

  .screen-size-extra-large-desktop .extra-small-desktop-show,
  .screen-size-large-desktop .extra-small-desktop-show,
  .screen-size-medium-desktop .extra-small-desktop-show,
  .screen-size-small-desktop .extra-small-desktop-show {
    display: none !important;
  }

  .screen-size-extra-large-desktop .small-desktop-show,
  .screen-size-large-desktop .small-desktop-show,
  .screen-size-medium-desktop .small-desktop-show {
    display: none !important;
  }

  .screen-size-extra-large-desktop .medium-desktop-show,
  .screen-size-large-desktop .medium-desktop-show {
    display: none !important;
  }

  .screen-size-extra-large-desktop .large-desktop-show {
    display: none !important;
  }

  .screen-size-phone .phone-show {
    display: block !important;
  }

  .screen-size-small-tablet .small-tablet-show,
  .screen-size-phone .small-tablet-show {
    display: block !important;
  }

  .screen-size-tablet .tablet-show,
  .screen-size-small-tablet .tablet-show,
  .screen-size-phone .tablet-show {
    display: block !important;
  }

  .screen-size-extra-small-desktop .extra-small-desktop-show,
  .screen-size-tablet .extra-small-desktop-show,
  .screen-size-small-tablet .extra-small-desktop-show,
  .screen-size-phone .extra-small-desktop-show {
    display: block !important;
  }

  .screen-size-small-desktop .small-desktop-show,
  .screen-size-extra-small-desktop .small-desktop-show,
  .screen-size-tablet .small-desktop-show,
  .screen-size-small-tablet .small-desktop-show,
  .screen-size-phone .small-desktop-show {
    display: block !important;
  }

  .screen-size-medium-desktop .medium-desktop-show,
  .screen-size-small-desktop .medium-desktop-show,
  .screen-size-extra-small-desktop .medium-desktop-show,
  .screen-size-tablet .medium-desktop-show,
  .screen-size-small-tablet .medium-desktop-show,
  .screen-size-phone .medium-desktop-show {
    display: block !important;
  }

  .screen-size-large-desktop .large-desktop-show,
  .screen-size-medium-desktop .large-desktop-show,
  .screen-size-small-desktop .large-desktop-show,
  .screen-size-extra-small-desktop .large-desktop-show,
  .screen-size-tablet .large-desktop-show,
  .screen-size-small-tablet .large-desktop-show,
  .screen-size-phone .large-desktop-show {
    display: block !important;
  }
</style>
